import {
  AICriterionOutcomeResult,
  AICriterionOutcomeSourceKind,
  AuditCriteriaPeriodKind,
  AuditCriterionValueAIStatus,
  AuditCriterionValueSource,
} from './auditCriteriaGeneric';

// PLAN-075: the criteria columns of a list and the latest answer per entity and column in a period (spec 9.1, 9.4).

export class AuditCriteriaValueColumn {
  lineageKey: string;

  requirement: string;

  // the criterion's short header (PLAN-CTI-082); empty shows the start of the requirement (getCriterionColumnTitle)
  columnName: string;

  // Recurring or OneTime
  mode: string;

  auditCriteriaSetId: number;

  // the subtype of the generic set the column comes from; undefined for the family set or an entity's own set
  subType: number | undefined;

  // true for a column only an entity's own set has (behind the chooser)
  specific: boolean;

  sortOrder: number;

  constructor() {
    this.lineageKey = '';
    this.requirement = '';
    this.columnName = '';
    this.mode = '';
    this.auditCriteriaSetId = 0;
    this.subType = undefined;
    this.specific = false;
    this.sortOrder = 0;
  }
}

// One answer: the compact cell of the API with its flags read out.
export class AuditCriteriaValue {
  resultNumber: number | undefined;

  resultDate: Date;

  taskId: number | undefined;

  hasComment: boolean;

  hasEvidence: boolean;

  source: AuditCriterionValueSource | undefined;

  aiStatus: AuditCriterionValueAIStatus;

  constructor() {
    this.resultNumber = undefined;
    this.resultDate = new Date(0);
    this.taskId = undefined;
    this.hasComment = false;
    this.hasEvidence = false;
    this.source = undefined;
    this.aiStatus = AuditCriterionValueAIStatus.None;
  }

  // a pass/fail criterion stores 1 = pass, 0 = fail
  isPass(): boolean {
    return this.resultNumber === 1;
  }

  isFail(): boolean {
    return this.resultNumber === 0;
  }
}

export class AuditCriteriaValues {
  columns: AuditCriteriaValueColumn[];

  // values per entity id, then per lineage key; an entity the user may not read and a column without an answer are absent
  values: Map<number, Map<string, AuditCriteriaValue>>;

  // the latest standing AI outcome per entity id and lineage key, whatever the period
  outcomes: Map<number, Map<string, AuditCriteriaValueOutcome>>;

  from: Date | undefined;

  to: Date | undefined;

  constructor() {
    this.columns = [];
    this.values = new Map();
    this.outcomes = new Map();
    this.from = undefined;
    this.to = undefined;
  }

  get(entityId: number, lineageKey: string): AuditCriteriaValue | undefined {
    return this.values.get(entityId)?.get(lineageKey);
  }

  getOutcome(entityId: number, lineageKey: string): AuditCriteriaValueOutcome | undefined {
    return this.outcomes.get(entityId)?.get(lineageKey);
  }
}

// The latest AI outcome of one criterion of one item that is still proposed or applied (spec 9.1).
export class AuditCriteriaValueOutcome {
  outcomeId: number = 0;

  proposedResult: AICriterionOutcomeResult = AICriterionOutcomeResult.Inconclusive;

  sourceKind: AICriterionOutcomeSourceKind = AICriterionOutcomeSourceKind.ProcessRun;

  // the process run "<process>:<run>", the agent turn or the check the outcome came from
  sourceRef: string = '';

  // empty when the automation no longer exists
  sourceName: string = '';

  created: Date = new Date();
}

// The period a list shows the criteria values for, kept per list in local storage.
export class AuditCriteriaPeriod {
  kind: AuditCriteriaPeriodKind;

  from: Date | undefined;

  to: Date | undefined;

  constructor(kind: AuditCriteriaPeriodKind = AuditCriteriaPeriodKind.Latest, from?: Date, to?: Date) {
    this.kind = kind;
    this.from = from;
    this.to = to;
  }

  isEqual(other: AuditCriteriaPeriod): boolean {
    return (
      this.kind === other.kind &&
      this.from?.getTime() === other.from?.getTime() &&
      this.to?.getTime() === other.to?.getTime()
    );
  }
}

// Keep only the entities whose answer to one criterion is a pass, a fail, or missing in the period.
export enum AuditCriteriaResultFilter {
  Pass = 'pass',
  Fail = 'fail',
  None = 'none',
}
